/**
 * Adventure Preview Header Component
 * 
 * Displays the adventure header information in the StepThree preview,
 * including name, description, base pricing info, and live preview status.
 * 
 * @author Wildlife Project Team
 * @version 2.1.0
 */

import React from 'react';
import { DollarSign } from 'lucide-react';
import { CURRENCY_OPTIONS } from '../constants';
import { getBasePriceTypeLabel } from '../hooks/basePricingCalculator';

interface AdventurePreviewHeaderProps {
  adventureName: string;
  adventureDescription: string;
  basePrice: number;
  basePriceType: 'fixed' | 'per_person' | 'per_day' | 'per_person_day';
  currency: string;
}

export function AdventurePreviewHeader({
  adventureName,
  adventureDescription,
  basePrice,
  basePriceType,
  currency
}: AdventurePreviewHeaderProps) {
  const currencySymbol = CURRENCY_OPTIONS.find(c => c.value === currency)?.symbol || '$';

  return (
    <div className="w-full bg-gradient-to-r from-primary/5 to-green-50 rounded-lg p-6 mb-6 border border-green-100">
      <h4 className="text-xl font-bold text-primary mb-2">
        {adventureName || 'Your Adventure Name'}
      </h4>
      <p className="text-gray-600">
        {adventureDescription || 'Your adventure description will appear here.'}
      </p>
      <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
        <span className="flex items-center gap-1">
          <DollarSign className="w-4 h-4" />
          Base: {currencySymbol}{basePrice.toFixed(2)} ({getBasePriceTypeLabel(basePriceType)})
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          Live Preview
        </span>
      </div>
    </div>
  );
} 